<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onServerPrefetch, useSSRContext, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import AgencyDirectoryBreadcrumbs from '@/components/Breadcrumbs/AgencyDirectoryBreadcrumbs.vue';
import { isPlatformHg, isPlatformIs24 } from '@/helpers/Platform/platform';
import { isPageIndexable } from '@/helpers/SEO/isPageIndexable';
import { countryTextMapping } from '@/helpers/SEO/metaInfoHelper';
import {
  sanitizeListingTypeParam,
  sanitizeSortByParam,
} from '@/helpers/String/sanitizeQueryParams';
import AgencyDirectorySeoLinks from '@/pages/AgencyDirectoryPage/AgencyDirectorySeoLinks.vue';
import AgencyResultList from '@/pages/AgencyDirectoryPage/AgencyResultList.vue';
import HeroBanner from '@/pages/AgencyDirectoryPage/HeroBanner.vue';
import { getBasePathWithLocation } from '@/router';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { LoadState } from '@/stores/types';
import {
  buildAgencyDirectoryPageViewPayload,
  trackAgencyDirectoryPageView,
} from '@/tracking/tracking';
import { HgSupportedLanguage } from '@smg-real-estate/domus';

const { t } = useI18n();
const language = useLocale();
const route = useRoute();
const ssrContext = useSSRContext();

const agencyProfilesStore = useAgencyProfilesStore();
const {
  agencies,
  agenciesLoadState,
  totalAgenciesCount,
  agencyListCurrentPage,
  pageCount,
  searchedLocations,
} = storeToRefs(agencyProfilesStore);

const currentPage = computed(() => {
  const page = parseInt((route.query.page as string) ?? 1, 10);

  if (isNaN(page) || page < 1) {
    return 1;
  }

  return page;
});

const filterLocations = computed(() => {
  const locations = [];

  if (route.params.location) {
    locations.push(route.params.location as string);
  }

  if (route.query.loc) {
    locations.push(...(route.query.loc as string).split(','));
  }

  return locations;
});

const includeChildrenAgencies = computed(() => route.query.includeChildren === 'true');
const listingType = computed(() => sanitizeListingTypeParam(route.query.listingType));
const sortingOption = computed(() => sanitizeSortByParam(route.query.sortBy));
onServerPrefetch(async () => {
  await agencyProfilesStore.changeUrlParams({
    geoLocations: filterLocations.value,
    locale: language.value,
    includeChildren: includeChildrenAgencies.value,
    currentPage: currentPage.value,
    listingType: listingType.value,
    sortBy: sortingOption.value,
  });

  if (ssrContext) {
    const pageTrackingContext = buildAgencyDirectoryPageViewPayload({
      agencies: agencies.value,
      filterLocations: filterLocations.value,
      totalAgenciesCount: totalAgenciesCount.value,
      pageCount: pageCount.value,
      currentPage: currentPage.value,
      language: language.value,
      listingType: listingType.value,
      sortingOption: sortingOption.value,
    });
    ssrContext.pageTrackingContext = JSON.stringify(pageTrackingContext);
  }
});

watch(
  [currentPage, filterLocations, includeChildrenAgencies, listingType, sortingOption],
  async () => {
    await agencyProfilesStore.changeUrlParams({
      geoLocations: filterLocations.value,
      locale: language.value,
      includeChildren: includeChildrenAgencies.value,
      currentPage: currentPage.value,
      listingType: listingType.value,
      sortBy: sortingOption.value,
    });

    trackAgencyDirectoryPageView({
      agencies: agencies.value,
      filterLocations: filterLocations.value,
      totalAgenciesCount: totalAgenciesCount.value,
      pageCount: pageCount.value,
      currentPage: currentPage.value,
      language: language.value,
      listingType: listingType.value,
      sortingOption: sortingOption.value,
    });
  },
);
const generateLocationsForSeo = () => {
  return searchedLocations.value && searchedLocations.value.length > 0
    ? searchedLocations.value.map((item) => item.names[language.value]).join(',')
    : countryTextMapping[language.value];
};

const generateCanonicalUrlForSeo = () => {
  const pageNumber = currentPage.value > 1 ? `?page=${currentPage.value}` : '';
  return `${import.meta.env.VUE_APP_BASE_URL}${route.path}${pageNumber}`;
};

const localizeLanguage = (locale: HgSupportedLanguage) => {
  return locale === HgSupportedLanguage.EN ? locale : `${locale}-CH`;
};

const shouldDeIndex = computed(() => !isPageIndexable(searchedLocations.value, currentPage.value));
const seoLinks = computed(() => [
  {
    rel: 'canonical' as const,
    href: generateCanonicalUrlForSeo(),
  },
  ...Object.values(HgSupportedLanguage).map((lang) => ({
    rel: 'alternate' as const,
    hreflang: localizeLanguage(lang),
    href: `${import.meta.env.VUE_APP_BASE_URL}${getBasePathWithLocation(lang, searchedLocations.value?.[0]?.urlNames?.[lang])}`,
  })),
  {
    rel: 'alternate' as const,
    hreflang: 'x-default',
    href: `${import.meta.env.VUE_APP_BASE_URL}${getBasePathWithLocation(HgSupportedLanguage.DE, searchedLocations.value?.[0]?.urlNames?.[HgSupportedLanguage.DE])}`,
  },
]);

useHead({
  title: () =>
    currentPage.value > 1
      ? t('seo.pageTitleWithPageLocationSpecific', {
          page: currentPage.value,
          location: generateLocationsForSeo(),
        })
      : t('seo.pageTitleLocationSpecific', { location: generateLocationsForSeo() }),
  meta: () => [
    {
      name: 'description',
      content: () =>
        t('seo.pageDescriptionLocationSpecific', { location: generateLocationsForSeo() }),
    },
    ...(shouldDeIndex.value
      ? [
          { name: 'robots', content: 'noindex, nofollow' }, // Meta robots tag (for HTML)
          { 'http-equiv': 'x-robots-tag', content: 'noindex, nofollow' }, // X-Robots-Tag (for HTTP headers - works better for some crawlers)
        ]
      : []),
  ],
  link: seoLinks,
});
</script>

<template>
  <div class="layout-container">
    <AgencyDirectoryBreadcrumbs v-if="isPlatformIs24()" class="breadcrumbs" />
  </div>

  <HeroBanner :locationsForSeo="generateLocationsForSeo()" />

  <div class="layout-container">
    <AgencyResultList
      :loadingAgencies="agenciesLoadState === LoadState.LOADING"
      :agencies="agencies"
      :totalAgenciesCount="totalAgenciesCount"
      :agencyListCurrentPage="agencyListCurrentPage"
      :pageCount="pageCount"
      :language="language"
      :locationsForSeo="generateLocationsForSeo()"
    />
    <AgencyDirectoryBreadcrumbs v-if="isPlatformHg()" class="breadcrumbs" />
    <AgencyDirectorySeoLinks />
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/variables' as *;

.breadcrumbs {
  margin-top: $space-150;
}
</style>
